import { useMemo, useState } from 'react';
import { X as CloseIcon } from 'lucide-react';
import { TranslationsKeys } from 'types/translation';
import { SurveyResponse } from 'types/surveyResponse';
import { useTranslate } from 'hooks/useTranslate';
import { FullscreenContainer } from 'components/FullscreenContainer';
import { Button } from 'components/ui/Button';
import { Dropdown } from 'components/ui/Dropdown';
import styles from '../MySurveys.module.scss';

export interface FilterValues {
  dateFrom?: Date;
  dateTo?: Date;
  businessArea?: string;
  country?: string;
  visitType?: string;
}

interface IProps {
  filter: FilterValues;
  allSurveys: SurveyResponse[];
  onFilterChange: (filterValues: FilterValues) => void;
  onClose: () => void;
}

export const SurveyResponsesFilters = ({ filter, allSurveys, onFilterChange, onClose }: IProps) => {
  const [selectedFilter, setSelectedFilter] = useState({ ...filter });
  const { t } = useTranslate();
  
  const allBusinessAreas = useMemo(() => [
    ...new Set(allSurveys.map(s => s.customer?.businessAreaCode)
      .filter(Boolean).map(String).toSorted((a, b) => a.localeCompare(b)))
  ], allSurveys);
  const allCountries = useMemo(() => [
    ...new Set(allSurveys.map(s => s.customer?.country)
      .filter(Boolean).map(String).toSorted((a, b) => a.localeCompare(b)))
  ], allSurveys);
  const allVisitTypes = useMemo(() => [
    ...new Set(allSurveys.map(s => s.visit?.visitTypeCode)
      .filter(Boolean).map(String).toSorted((a, b) => a.localeCompare(b)))
  ], allSurveys);
  
  return (
    <FullscreenContainer>
      <div className={styles.mySurveysFilters}>
        <div className={styles.mySurveysFiltersHeading}>
          <span>{t(TranslationsKeys.common_filters)}</span>
          <button onClick={onClose} className={styles.mySurveysFiltersClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.mySurveysFiltersContainer}>
          <div className={styles.mySurveysFiltersContainerHeading}>
            {t(TranslationsKeys.mySurveysPage_myVisits_title)}
          </div>
          <div className={styles.mySurveysFiltersContainerItems}>
            {/* TODO: date from/to, requires datepicker */}
            <div className={styles.mySurveysFiltersContainerItemsItem}>
              <label htmlFor="filterBusinessArea">{t(TranslationsKeys.common_businessArea)}</label>
              <Dropdown id="filterBusinessArea"
                        options={[{ label: ' ', value: '' }, ...allBusinessAreas.map(v => ({ label: v, value: v }))]} 
                        value={selectedFilter.businessArea || ''} 
                        onChange={v => setSelectedFilter({ ...selectedFilter, businessArea: v })} />
            </div>
            <div className={styles.mySurveysFiltersContainerItemsItem}>
              <label htmlFor="filterCountry">{t(TranslationsKeys.common_country)}</label>
              <Dropdown id="filterCountry"
                        options={[{ label: ' ', value: '' }, ...allCountries.map(v => ({ label: v, value: v }))]}
                        value={selectedFilter.country || ''}
                        onChange={v => setSelectedFilter({ ...selectedFilter, country: v })} />
            </div>
            <div className={styles.mySurveysFiltersContainerItemsItem}>
              <label htmlFor="filterVisitType">{t(TranslationsKeys.surveyIntroPage_customerVisit_visitType)}</label>
              <Dropdown id="filterVisitType"
                        options={[{ label: ' ', value: '' }, ...allVisitTypes.map(v => ({ label: v, value: v }))]}
                        value={selectedFilter.visitType || ''}
                        onChange={v => setSelectedFilter({ ...selectedFilter, visitType: v })} />
            </div>
          </div>
        </div>
        <div className={styles.mySurveysFiltersActions}>
          <Button variant="secondary" onClick={onClose}>
            {t(TranslationsKeys.common_cancel)}
          </Button>
          <Button
            variant="primary"
            onClick={() => onFilterChange(selectedFilter)}>
            {t(TranslationsKeys.mySurveysPage_showVisits)}
          </Button>
        </div>
      </div>
    </FullscreenContainer>
  );
};
